import { createContext, useContext, useEffect, useState, useCallback, ReactNode, useRef } from "react"
import { getAuth, User } from "firebase/auth"
import { firebaseApp } from "~/client/firebase.client"
import { AUTH_PATHS } from "~/lib/consts"

interface AuthContextType {
  user: User | null
  isLoading: boolean
  logout: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  
  // Refs to prevent duplicate token syncs
  const syncInProgressRef = useRef(false)
  const lastSyncedTokenRef = useRef<string | null>(null)
  const pendingTokenRef = useRef<string | null>(null)

  const syncTokenToServer = useCallback(async (idToken: string) => {
    if (lastSyncedTokenRef.current === idToken) {
      return
    }

    // If a sync is already in progress, stash this token as "next in line"
    if (syncInProgressRef.current) {
      pendingTokenRef.current = idToken
      return
    }

    syncInProgressRef.current = true
    pendingTokenRef.current = null

    try {
      const formData = new FormData()
      formData.set("idToken", idToken)
      
      const response = await fetch("/resources/session", {
        method: "POST",
        body: formData,
      })
      
      if (!response.ok) {
        throw new Error(`Failed to sync session: ${response.status} ${response.statusText}`)
      }
      
      lastSyncedTokenRef.current = idToken
    } catch (error) {
      console.error("Failed to sync token with server:", error)
    } finally {
      syncInProgressRef.current = false
      
      // If a new token arrived while we were syncing, process it now
      if (pendingTokenRef.current && pendingTokenRef.current !== lastSyncedTokenRef.current) {
        const nextToken = pendingTokenRef.current
        pendingTokenRef.current = null
        setTimeout(() => syncTokenToServer(nextToken), 0)
      }
    }
  }, [])

  useEffect(() => {
    const auth = getAuth(firebaseApp)
    
    // Firebase automatically refreshes tokens ~5 minutes before expiry (tokens last 1 hour)
    // onIdTokenChanged fires on: login, logout, and automatic token refresh
    const unsubscribe = auth.onIdTokenChanged(async (user) => {
      setUser(user)
      
      if (user) {

        // Auth screens have no Console session to refresh, and only the
        // /login action may start one.
        const onAuthScreen = AUTH_PATHS.includes(window.location.pathname)
        if (user.emailVerified && !onAuthScreen) {
          try {
            const idToken = await user.getIdToken()
            await syncTokenToServer(idToken)
            setIsLoading(false)
          } catch (error) {
            console.error("Failed to sync token with server:", error)
            setIsLoading(false)
          }
        } else {
          setIsLoading(false)
        }
      } else {
        setIsLoading(false)
      }
    })
    
    return () => { 
      unsubscribe()
    }
  }, [syncTokenToServer])

  const logout = useCallback(async () => {
    try {
      const auth = getAuth(firebaseApp)
      // Sign out from Firebase
      await auth.signOut()
      setUser(null)
      
      // Server-side logout
      await fetch("/logout", { method: "POST" })
      
      // Redirect to login
      window.location.href = "/"
    } catch (error) {
      console.error("Logout error:", error)
      // Force redirect even on error
      window.location.href = "/"
    }
  }, [])

  return (
    <AuthContext.Provider value={{ user, isLoading, logout }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider")
  }
  return context
}


